/**
 * Which term a teacher's cross-class views open on.
 *
 * A year's classes all exist at once, so these views open on a single term —
 * the one containing today, else the next to start (see defaultTermId) — rather
 * than every term at once. The other terms stay one click away.
 */
export interface TermOption {
  id: string;
  name: string;
  startsOn?: string | null;
  endsOn?: string | null;
}

export const ALL_TERMS = 'all';

/**
 * All these need of a class is which term it sits in, so they ask for that and
 * nothing else — the sidebar lists classes from a much smaller query than the
 * Teaching pages do, and both should be able to use the same rules.
 */
export interface ClassWithTerm {
  term?: { id: string; name: string; startsOn?: unknown; endsOn?: unknown } | null;
}

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** The terms this teacher has classes in, in the order the classes came back. */
export function termsFromClasses(classes: readonly ClassWithTerm[]): TermOption[] {
  const seen = new Map<string, TermOption>();
  for (const offering of classes) {
    const term = offering.term;
    if (!term || seen.has(term.id)) continue;
    seen.set(term.id, {
      id: term.id,
      name: term.name,
      startsOn: term.startsOn ? String(term.startsOn) : null,
      endsOn: term.endsOn ? String(term.endsOn) : null,
    });
  }
  return [...seen.values()];
}

/**
 * The term today falls inside. Between quarters — a break, or a year not yet
 * started — nothing is current, and the caller decides what to fall back to.
 */
export function currentTermId(terms: readonly TermOption[], today = localDateString()): string | null {
  const match = terms.find(
    (term) => term.startsOn && term.endsOn && today >= term.startsOn && today <= term.endsOn
  );
  return match?.id ?? null;
}

/**
 * What to open on: today's term when there is one, otherwise the next term to
 * start, otherwise the first we know about. Never "all".
 */
export function defaultTermId(terms: readonly TermOption[], today = localDateString()): string | null {
  if (terms.length === 0) return null;
  const current = currentTermId(terms, today);
  if (current) return current;

  const upcoming = terms
    .filter((term) => term.startsOn && term.startsOn > today)
    .sort((first, second) => String(first.startsOn).localeCompare(String(second.startsOn)))[0];
  return upcoming?.id ?? terms[0].id;
}

export function classesInTerm<T extends ClassWithTerm>(
  classes: readonly T[],
  termId: string
): T[] {
  if (termId === ALL_TERMS) return [...classes];
  return classes.filter((offering) => offering.term?.id === termId);
}
